export default function FeaturesSection() {
  const features = [
    {
      icon: 'ri-shield-check-line',
      title: '企业级安全保障',
      description: '端到端加密传输，多层权限控制体系，审计日志全程可追溯，满足等保合规要求，让每一份文件都安全可控。',
      variant: 'dark',
    },
    {
      icon: 'ri-git-branch-line',
      title: '智能版本管理',
      description: '自动保存文件历史版本，一键对比差异，随时回溯到任意历史节点，再也不用担心误操作覆盖重要内容。',
      variant: 'image',
      imageUrl: 'https://readdy.ai/api/search-image?query=Clean%20modern%20dashboard%20interface%20showing%20file%20version%20timeline%20history%2C%20minimalist%20UI%20design%20with%20soft%20beige%20background%2C%20corporate%20SaaS%20product%20screenshot%2C%20subtle%20shadows%2C%20professional%20atmosphere&width=800&height=600&seq=feature-version-fs&orientation=landscape',
    },
    {
      icon: 'ri-team-line',
      title: '实时团队协作',
      description: '支持多人同时在线编辑与批注，实时同步更新，内置评论与审批流程，让团队协作高效流畅、井然有序。',
      variant: 'light',
      stat: '99.9%',
      statLabel: '服务可用性',
    },
  ];

  return (
    <section id="features" className="w-full bg-background-100 py-14 md:py-20 px-4 md:px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 mb-10 md:mb-14">
          <div className="lg:w-1/3">
            <p className="font-label text-xs md:text-sm font-medium tracking-widest text-foreground-500 uppercase">
              产品特性
            </p>
            <p className="font-label text-xs md:text-sm font-medium tracking-widest text-foreground-500 uppercase mt-1">
              为什么选择我们
            </p>
          </div>
          <div className="lg:w-2/3">
            <h3 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
              重新定义文件管理
              <br />
              的<span className="italic">可能性</span>
            </h3>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {features.map((feature, index) => (
            <FeatureCard key={index} feature={feature} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureCard({
  feature,
}: {
  feature: {
    icon: string;
    title: string;
    description: string;
    variant: string;
    imageUrl?: string;
    stat?: string;
    statLabel?: string;
  };
}) {
  if (feature.variant === 'dark') {
    return (
      <div className="rounded-2xl bg-foreground-900 p-6 md:p-8 flex flex-col justify-between min-h-[340px] md:min-h-[380px] hover:scale-[1.02] transition-transform duration-300">
        <div>
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-foreground-700/40 flex items-center justify-center">
            <i className={`${feature.icon} text-background-50 text-lg md:text-xl`}></i>
          </div>
          <h4 className="font-heading text-xl md:text-2xl font-semibold text-background-50 mt-6 mb-3">
            {feature.title}
          </h4>
          <p className="text-foreground-300 text-sm leading-relaxed">
            {feature.description}
          </p>
        </div>
      </div>
    );
  }

  if (feature.variant === 'image') {
    return (
      <div className="rounded-2xl overflow-hidden relative min-h-[340px] md:min-h-[380px] hover:scale-[1.02] transition-transform duration-300">
        <div className="absolute inset-0">
          <img
            src={feature.imageUrl}
            alt={feature.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
        <div className="absolute top-4 left-4">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-foreground-900/60 backdrop-blur-sm text-background-50 text-xs font-medium">
            <i className={`${feature.icon} text-xs`}></i>
            {feature.title}
          </span>
        </div>
        <div className="absolute bottom-6 left-6 right-6">
          <h4 className="font-heading text-xl md:text-2xl font-semibold text-background-50 mb-2">
            {feature.title}
          </h4>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-background-50 p-6 md:p-8 flex flex-col min-h-[340px] md:min-h-[380px] hover:scale-[1.02] transition-transform duration-300">
      <div className="flex-1">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-accent-100 flex items-center justify-center">
          <i className={`${feature.icon} text-accent-700 text-lg md:text-xl`}></i>
        </div>
        <h4 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mt-6 mb-3">
          {feature.title}
        </h4>
        <p className="text-foreground-600 text-sm leading-relaxed">
          {feature.description}
        </p>
      </div>
      {feature.stat && (
        <div className="mt-6 pt-4 border-t border-background-200/70">
          <p className="font-heading text-3xl md:text-4xl font-bold text-accent-600">
            {feature.stat}
          </p>
          <p className="text-foreground-500 text-xs mt-1">{feature.statLabel}</p>
        </div>
      )}
    </div>
  );
}